// ==========================================
// 1. CRITICAL ENVIRONMENT PATCHES
// ==========================================

if (typeof window !== 'undefined' && (window as any).__PRERENDER_INJECTED?.isPrerendering) {
  // Fixes the 'DOMException: Not supported' error caused by missing history state support in JSDOM
  if (window.history && !window.history.pushState) {
    window.history.pushState = (() => { }) as any;
    window.history.replaceState = (() => { }) as any;
  }

  // Safeguard for modern layout APIs that JSDOM lacks
  if (!window.matchMedia) {
    window.matchMedia = (query) => ({
      matches: false,
      media: query,
      onchange: null,
      addListener: () => { },
      removeListener: () => { },
      addEventListener: () => { },
      removeEventListener: () => { },
      dispatchEvent: () => false,
    });
  }
}

// ==========================================
// 2. STANDARD APPLICATION IMPORTS
// ==========================================
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import App from './App.tsx';

// Bundled Local Fonts
import '@fontsource-variable/dm-sans';
import '@fontsource/dm-serif-display';
import '@fontsource/dm-serif-display/400-italic.css';

import './index.css';

import { SHOULD_USE_MOCKS, IS_VALID_CLERK_KEY } from './lib/clerk';

function ConfigError() {
  return (
    <div className="h-screen w-screen flex items-center justify-center bg-gray-100 p-6 dark:bg-slate-800">
      <div className="max-w-md w-full bg-white border border-gray-300 rounded-xl shadow-sm p-8 text-center dark:bg-slate-900">
        <div className="w-16 h-16 bg-red-50 text-red-700 rounded-full flex items-center justify-center mx-auto mb-6">
          <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z" /><path d="M12 9v4" /><path d="M12 17h.01" /></svg>
        </div>
        <h1 className="text-xl font-semibold text-gray-900 mb-2 dark:text-white">Live Authentication Required</h1>
        <p className="text-sm text-gray-600 mb-6">
          Mock mode is disabled, but your Clerk Publishable Key is missing or invalid. Please add it to your environment variables or re-enable mocks to continue.
        </p>
        <div className="bg-gray-50 rounded-lg p-4 text-left mb-6 dark:bg-slate-950">
          <p className="text-xs font-mono text-gray-500 uppercase tracking-wider mb-2">Required Variable</p>
          <code className="text-xs bg-white border border-gray-200 px-2 py-1 rounded block dark:bg-slate-900">
            VITE_CLERK_PUBLISHABLE_KEY
          </code>
        </div>
        <p className="text-xs text-gray-400">
          You can find this key in your <a href="https://dashboard.clerk.com" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">Clerk Dashboard</a>.
        </p>
      </div>
    </div>
  );
}

const isPrerendering = !!(window as any).__PRERENDER_INJECTED?.isPrerendering;
const canRender = isPrerendering || IS_VALID_CLERK_KEY || SHOULD_USE_MOCKS;
console.log('BUILD_DEBUG: isPrerendering =', isPrerendering); // Check your console output!
console.log('BUILD_DEBUG: canRender =', canRender);
console.log('BUILD_DEBUG: IS_VALID_CLERK_KEY =', IS_VALID_CLERK_KEY);
console.log('BUILD_DEBUG: SHOULD_USE_MOCKS =', SHOULD_USE_MOCKS);

const rootElement = document.getElementById('root')!;

// Clean up prerendered SEO tags to prevent react-helmet-async from duplicating them
// Helmet's client-side hydration often fails if attributes aren't an exact match, so we just clear the slate.
if (!isPrerendering && typeof document !== 'undefined') {
  const prerenderedTags = document.querySelectorAll('title, meta[data-rh="true"], link[data-rh="true"]');
  prerenderedTags.forEach(tag => tag.remove());
}

try {
  createRoot(rootElement).render(
    <StrictMode>
      {canRender ? (
        <App isPrerendering={isPrerendering} />
      ) : (
        <ConfigError />
      )}
    </StrictMode>,
  );
} catch (error) {
  // If this logs during 'npm run build', we have found the culprit.
  console.error('CRITICAL BUILD ERROR:', error);
  // Still fire the event so the build doesn't hang forever
  document.dispatchEvent(new Event('prerender-ready'));
}

// Immediately fire the fallback event if we cannot render so the compilation doesn't freeze
if (!canRender) {
  document.dispatchEvent(new Event('prerender-ready'));
}